import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

const LOCAL_KEY = "bytajobb_tour_completed_at";

export function useOnboardingTour(user: User | null) {
  const [shouldShow, setShouldShow] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (!user) {
        // Anonym: kolla localStorage
        const local = localStorage.getItem(LOCAL_KEY);
        if (!cancelled) {
          setShouldShow(!local);
          setChecked(true);
        }
        return;
      }

      try {
        const { data } = await supabase
          .from("user_preferences")
          .select("tour_completed_at")
          .eq("user_id", user.id)
          .maybeSingle();

        if (cancelled) return;

        if (!data?.tour_completed_at) {
          setShouldShow(true);
        } else {
          setShouldShow(false);
        }
      } catch (e) {
        console.error("Tour check failed:", e);
      } finally {
        if (!cancelled) setChecked(true);
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const markComplete = useCallback(async () => {
    setShouldShow(false);
    const now = new Date().toISOString();
    localStorage.setItem(LOCAL_KEY, now);

    if (!user) return;
    try {
      await supabase
        .from("user_preferences")
        .upsert({ user_id: user.id, tour_completed_at: now }, { onConflict: "user_id" });
    } catch (e) {
      console.error("Tour persist failed:", e);
    }
  }, [user]);

  const restart = useCallback(() => {
    setShouldShow(true);
  }, []);

  return { shouldShow, checked, markComplete, restart };
}
